/**
 * QuickEditCard — the level-editor layer: a small card attached to the
 * selected widget on the canvas, holding its one to three most used fields
 * (the full form is "All settings", the Inspector).
 *
 * The card is a Duro Popover with a VIRTUAL anchor — the widget's on-screen
 * rect — portalled out of the canvas, so it stays at 100 % size whatever the
 * canvas zoom. It follows its widget: while open it re-measures once per
 * animation frame and repositions only when the rect moved, which covers
 * pan, zoom, autogrow and a design system re-rendering the widget alike (the
 * canvas publishes zoom but not pan, and a ResizeObserver sees no
 * transform). On a phone there is no room beside the widget: the card is a
 * bottom Drawer.
 */
import { useEffect, useRef, useState } from "react";
import {
  Button,
  Drawer,
  Popover,
  Stack,
  type PopoverHandle,
} from "@duro-app/ui";
import { useGridStore } from "@fredericrous/grid";
import {
  closeQuickEdit,
  flushPendingEdits,
  openSettings,
  registeredWidgetQuickEdit,
  registeredWidgetSettings,
  useIsMobile,
  useQuickEditTarget,
  useSelectionView,
  widgetKindName,
} from "@website-builder/editor";

function itemElement(itemId: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(
    `[data-grid-item="${CSS.escape(itemId)}"]`,
  );
}

export function QuickEditCard(): React.ReactElement | null {
  const target = useQuickEditTarget();
  const view = useSelectionView();
  const isMobile = useIsMobile();
  const item = useGridStore((s) =>
    target ? s.grids[target.gridId]?.items[target.itemId] : undefined,
  );
  const popover = useRef<PopoverHandle | null>(null);
  // Whether the widget is on screen: zoomed far out, the canvas draws the
  // page as a thumbnail with no widget elements, and the card waits hidden.
  const [anchored, setAnchored] = useState(true);

  // The card belongs to its widget: another selection, or none, closes it.
  useEffect(() => {
    if (!target) return;
    const same =
      view.kind === "widget" &&
      view.gridId === target.gridId &&
      view.itemId === target.itemId;
    if (!same) {
      flushPendingEdits();
      closeQuickEdit();
    }
  }, [view, target]);

  useEffect(() => {
    if (target && !item) closeQuickEdit();
  }, [target, item]);

  // Follow the widget: one rect read per frame, a reposition only on change.
  useEffect(() => {
    if (!target || isMobile) return;
    // Never a measured key: the first frame always reports, found or not.
    let last = "unmeasured";
    let frame = 0;
    const tick = () => {
      const r = itemElement(target.itemId)?.getBoundingClientRect();
      const key = r ? `${r.left}|${r.top}|${r.width}|${r.height}` : "";
      if (key !== last) {
        last = key;
        setAnchored(r !== undefined);
        popover.current?.reposition();
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, isMobile]);

  if (!target || !item) return null;
  if (!isMobile && !anchored) return null;
  const Form = registeredWidgetQuickEdit(item.contentType);
  if (!Form) return null;
  const title = widgetKindName(
    item.contentType,
    item.contentProps as Record<string, unknown> | undefined,
  );
  const close = (): void => {
    flushPendingEdits();
    closeQuickEdit();
  };
  const body = (
    // eslint-disable-next-line duro/no-raw-html-element -- the settings-surface marker is a data attribute (the editor's key guard finds it with closest()), which react-strict-dom's html.* cannot forward
    <div
      data-wb-settings-surface=""
      onKeyDown={(e) => {
        if (e.key === "Enter" && !(e.target instanceof HTMLTextAreaElement))
          flushPendingEdits();
      }}
    >
      <Stack gap="sm">
        <Form gridId={target.gridId} itemId={target.itemId} />
        {registeredWidgetSettings(item.contentType) && (
          <Button
            variant="link"
            size="small"
            onClick={() => {
              flushPendingEdits();
              openSettings(target.gridId, target.itemId);
            }}
          >
            All settings
          </Button>
        )}
      </Stack>
    </div>
  );

  if (isMobile)
    return (
      <Drawer.Root
        open
        anchor="bottom"
        onOpenChange={(next) => {
          if (!next) close();
        }}
      >
        <Drawer.Portal size="sm">
          <Drawer.Header>
            <Drawer.Title>{title}</Drawer.Title>
            <Drawer.Close />
          </Drawer.Header>
          <Drawer.Body>{body}</Drawer.Body>
        </Drawer.Portal>
      </Drawer.Root>
    );

  return (
    <Popover.Root
      ref={popover}
      open
      onOpenChange={(next) => {
        if (!next) close();
      }}
      side="bottom"
      align="start"
      anchor={() =>
        itemElement(target.itemId)?.getBoundingClientRect() ??
        new DOMRect(0, 0, 0, 0)
      }
      // A press on the widget itself is not "outside": it keeps the card
      // while you work on that widget.
      ignore={[() => itemElement(target.itemId)]}
    >
      <Popover.Popup label={`${title} — quick edit`}>{body}</Popover.Popup>
    </Popover.Root>
  );
}
